// react
import { animated, useChain, useSpring, useSpringRef, useTransition } from "@react-spring/web";
// components
import React, { PropsWithChildren, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import ExtendedPanel from "./ExtendedPanel";

import usePageWidthBreakpoint from "../../hooks/usePageWidthBreakpoint";

// styles
import css from "./Panel.module.scss";

export interface PanelProps {
	isOpen: boolean;
	onClose?: () => void;
}

interface BaseProps extends PanelProps {
	extendedPanelOpen?: boolean;
	extendedPanelContent?: React.ReactNode;
	side?: "left" | "right" | "top" | "bottom";
	width?: number;
}

const DRAWER_TRANSLATE_DIRECTIONS = {
	left: {
		from: { translateX: "-100%" },
		enter: { translateX: "0" },
		leave: { translateX: "-100%" },
	},
	right: {
		from: { translateX: "100%" },
		enter: { translateX: "0" },
		leave: { translateX: "100%" },
	},
	top: {
		from: { translateY: "-100%" },
		enter: { translateY: "0" },
		leave: { translateY: "-100%" },
	},
	bottom: {
		from: { translateY: "100%" },
		enter: { translateY: "0" },
		leave: { translateY: "100%" },
	},
};

/**
 * @deprecated Use the Panel component from the py-components package instead
 * Panel component fixed position on the left, with extendable side-panel
 * for sub-options such as selecting conditions for a filter.
 *
 */
const Panel = (props: PropsWithChildren<BaseProps>) => {
	const {
		children,
		extendedPanelOpen,
		extendedPanelContent,
		isOpen,
		onClose = () => {},
		side = "left",
		width = 420,
	} = props;
	// create the root that we will mount the modal to using a portal
	const panelRoot = useRef(document.createElement("div"));

	// If the page is mobile, we want to render the panel as a full screen slideout otherwise it's 420 px wide
	const { isMobile } = usePageWidthBreakpoint(width);

	const drawerTranslation = DRAWER_TRANSLATE_DIRECTIONS[side];

	const panelRef = useSpringRef();
	const transitions = useTransition(isOpen, {
		ref: panelRef,
		from: {
			opacity: 0,
			...drawerTranslation.from,
		},
		enter: {
			opacity: 1,
			...drawerTranslation.enter,
		},
		leave: {
			opacity: 0,
			...drawerTranslation.leave,
		},
		config: {
			tension: 350,
			friction: 30,
			mass: 0.5,
			clamp: true,
		},
	});

	const panelConfigs = extendedPanelOpen ? drawerTranslation.enter : drawerTranslation.leave;

	const extendedPanelRef = useSpringRef();
	const extendedPanelStyle = useSpring({
		ref: extendedPanelRef,
		...panelConfigs,
	});

	const extendedPanelOpenRef = useSpringRef();
	const extendedPanelVisibility = useSpring({
		ref: extendedPanelOpenRef,
		opacity: extendedPanelOpen && isOpen ? 1 : 0,
		config: {
			tension: 350,
			friction: 0,
			mass: 0.5,
			clamp: true,
		},
	});

	useChain(
		extendedPanelOpen && isOpen
			? [panelRef, extendedPanelOpenRef, extendedPanelRef]
			: [extendedPanelRef, extendedPanelOpenRef, panelRef]
	);

	// On mount we want to add a div to attach the panel to
	// On dismount we want to remove the div
	useEffect(() => {
		document.body.appendChild(panelRoot.current);

		return () => {
			panelRoot.current.remove();
		};
	}, []);

	return transitions(
		(style, isOpen) =>
			isOpen &&
			createPortal(
				<div className={`${css.panelContainer}`}>
					<div className={css.panel}>
						<animated.div
							style={{ ...style, opacity: undefined, width: isMobile ? "100%" : width }}
							className={`${css.panelMain} ${css[side]} ${isMobile && css.isMobile}`}
						>
							{children}
						</animated.div>
						<animated.div
							style={{ ...extendedPanelStyle, ...extendedPanelVisibility }}
							className={`${css.panelExtended} ${css[side]} ${
								extendedPanelOpen ? css.opened : css.closed
							}`}
						>
							<ExtendedPanel>{extendedPanelContent}</ExtendedPanel>
						</animated.div>
					</div>
					<animated.div style={{ opacity: style.opacity }} className={`${css.backdrop}`} onClick={onClose} />
				</div>,
				panelRoot.current
			)
	);
};

/**
 * @deprecated Use the Panel component from the py-components package instead
 */
export default Panel;
